/**
 * Editor tools — the brush registry.
 *
 * Every tool is a small stateless object whose only job is to mutate a `Level`
 * at one tile. Anything a tool needs to know about what the user currently has
 * in their hand (the loaded terrain, the selected object) arrives through
 * `ToolContext`, which the editor owns and mutates. That keeps this file as
 * close to pure data as a tool system can get, so the behaviour of the editor
 * can be reasoned about without reading the input plumbing.
 *
 * `apply()` returning false means "I declined" — out of bounds, occupied, no
 * object selected. The editor treats that as "do not start an undo entry",
 * which is what stops a stream of illegal clicks from filling the history with
 * snapshots that change nothing.
 */

import {
  blockUseCount,
  MAX_BLOCK_CONTENTS,
  type ObjectDef,
} from '../game/objects'
import type { Level, PlacedObject } from '../game/level'
import { Terrain } from '../game/types'

/**
 * Which layer a tool acts on. `utility` covers the tools that are neither —
 * they inspect the level, drag a rectangle, or move the start marker.
 */
export type ToolKind = 'terrain' | 'object' | 'utility'

/** Mutable per-stroke state shared with the tools. */
export interface ToolContext {
  /** Terrain the terrain brushes paint. */
  terrain: number
  /** Object the object brush places, or null when none is selected. */
  objectDef: ObjectDef | null
  /** What a rectangle fill lays down. */
  fillMode: 'terrain' | 'object'
  /** Eyedropper: report the terrain even where an object sits on top. */
  preferTerrain: boolean
  /** Written by the eyedropper, drained by the editor straight after apply. */
  pick: { terrain: number | null; object: string | null }
  /**
   * A one-shot user-facing message written by a tool and drained by the editor,
   * the same shape as `pick`. Only tools with something the palette hint cannot
   * say write one — "loaded a prism", "the block is full".
   */
  note: string | null
}

/** A fresh context with the default brush loaded. */
export function newToolContext(): ToolContext {
  return {
    terrain: Terrain.GROUND,
    objectDef: null,
    fillMode: 'terrain',
    preferTerrain: false,
    pick: { terrain: null, object: null },
    note: null,
  }
}

export interface Footprint {
  w: number
  h: number
}

export interface Tool {
  id: string
  name: string
  kind: ToolKind
  /** Terrain this tool paints, or `Terrain.EMPTY` when it paints no terrain. */
  cursorTile: number
  /** Short code drawn on the tool chip and the palette cell. */
  code: string
  /** One line of help, shown under the palette. */
  hint: string
  /** `KeyboardEvent.code` shortcut, or null. */
  key: string | null
  /** False for tools that only read the level (the eyedropper). */
  mutates: boolean
  /**
   * Offered on the palette's terrain tab.
   *
   * Load-bearing, not documentation: `TERRAIN_TOOLS` is derived from this flag,
   * so a terrain brush with the flag unset is a brush the palette cannot reach.
   */
  inPalette: boolean
  /** Chip / swatch colour. */
  color: string
  canApply(level: Level, tx: number, ty: number, ctx: ToolContext): boolean
  apply(level: Level, tx: number, ty: number, ctx: ToolContext): boolean
  footprint(ctx: ToolContext): Footprint
}

const ONE: Footprint = { w: 1, h: 1 }

// ── shared level predicates ───────────────────────────────────────────────

/** Is this tile inside the course? */
export function inBounds(level: Level, tx: number, ty: number): boolean {
  return tx >= 0 && ty >= 0 && tx < level.width && ty < level.height
}

/**
 * Does this tile block the player (terrain or a solid object)?
 *
 * A travelling platform counts, and it is not in either grid. `Level` keeps
 * `dynamic` objects out of the merged tile grid on purpose -- a moving platform
 * straddles a tile boundary as it moves, so baking it into the grid makes it two
 * rows tall half the time -- and collides it as a free AABB instead. So a start
 * placed on a lift, which the player can absolutely ride, was standing on
 * nothing as far as the merged grid was concerned, and the editor said so:
 * `START HAS NO FLOOR` on a course that plays. The predicate asks whether the
 * tile blocks the player, so it has to ask about the platforms too.
 */
export function solidAt(level: Level, tx: number, ty: number): boolean {
  if (level.isSolid(tx, ty) || level.isOneWay(tx, ty)) return true
  for (const o of level.objects) {
    if (!o.alive || o.def.dynamic !== true) continue
    if (
      tx >= o.tx &&
      ty >= o.ty &&
      tx < o.tx + o.def.w &&
      ty < o.ty + o.def.h
    ) {
      return true
    }
  }
  return false
}

/**
 * The topmost live object covering a tile, or null.
 *
 * `Level.objectAt` reads the merged occupancy grid, and that grid only records
 * objects with a solidity — a coin, a rivet, the goal flag and every other
 * `Solidity.NONE` object is invisible to it. An editor has to be able to pick
 * those up, so this scans the placement list instead. Later placements win,
 * matching the renderer's draw order.
 */
export function objectAtTile(
  level: Level,
  tx: number,
  ty: number,
): PlacedObject | null {
  for (let i = level.objects.length - 1; i >= 0; i--) {
    const o = level.objects[i]
    if (o === undefined || !o.alive) continue
    if (tx >= o.tx && ty >= o.ty && tx < o.tx + o.def.w && ty < o.ty + o.def.h)
      return o
  }
  return null
}

/** Is any live object (other than `ignore`) covering this tile? */
function occupiedAt(
  level: Level,
  tx: number,
  ty: number,
  ignore?: PlacedObject,
): boolean {
  for (const o of level.objects) {
    if (!o.alive || o === ignore) continue
    if (tx >= o.tx && ty >= o.ty && tx < o.tx + o.def.w && ty < o.ty + o.def.h)
      return true
  }
  return false
}

/**
 * Would a `w`x`h` object fit at this tile? One object per tile is the rule the
 * original editor uses, so any occupied tile blocks the placement, solid or
 * not. `ignore` lets the move tool test its own destination.
 */
export function fits(
  level: Level,
  tx: number,
  ty: number,
  w: number,
  h: number,
  ignore?: PlacedObject,
): boolean {
  for (let dy = 0; dy < h; dy++) {
    for (let dx = 0; dx < w; dx++) {
      const x = tx + dx
      const y = ty + dy
      if (!inBounds(level, x, y)) return false
      if (occupiedAt(level, x, y, ignore)) return false
    }
  }
  return true
}

// ── terrain brushes ───────────────────────────────────────────────────────

/**
 * A terrain brush. Note that it paints *its own* terrain id rather than the
 * brush's: the eraser is also reachable as a modifier (middle click, Alt+click)
 * without changing what the palette has selected, and a modifier that painted
 * the loaded terrain instead of erasing would be a nasty surprise.
 */
function terrainTool(
  id: string,
  name: string,
  terrain: number,
  code: string,
  key: string | null,
  color: string,
  hint: string,
): Tool {
  return {
    id,
    name,
    kind: 'terrain',
    cursorTile: terrain,
    code,
    hint,
    key,
    mutates: true,
    inPalette: true,
    color,
    canApply: (level, tx, ty) => inBounds(level, tx, ty),
    apply: (level, tx, ty) => {
      if (!inBounds(level, tx, ty)) return false
      level.setTerrain(tx, ty, terrain)
      return true
    },
    footprint: () => ONE,
  }
}

const groundTool = terrainTool(
  'ground',
  'Ground',
  Terrain.GROUND,
  'GD',
  null,
  '#a44200',
  'Terrain brush. Paint with the left button, drag to sweep.',
)

const eraseTool = terrainTool(
  'erase',
  'Terrain Eraser',
  Terrain.EMPTY,
  'ER',
  null,
  '#2a2a3a',
  'Erase terrain and objects. Drag to scrub; undo restores the stroke.',
)

const hardTool = terrainTool(
  'hard',
  'Alloy Block',
  Terrain.HARD,
  'HD',
  null,
  '#7c7c7c',
  'Indestructible structure block.',
)

const brickTool = terrainTool(
  'brick',
  'Cracked Tile',
  Terrain.BRICK,
  'BR',
  null,
  '#c05000',
  'Breakable brick. Head bumps knock it out.',
)

const platformTool = terrainTool(
  'platform',
  'Catwalk',
  Terrain.PLATFORM,
  'PF',
  null,
  '#d07830',
  'One-way platform: lands from above, passes from below.',
)

const slopeLTool = terrainTool(
  'slope-l',
  'Slope Left',
  Terrain.SLOPE_L,
  'SL',
  null,
  '#6b8cff',
  'Slope rising to the right. Pair with a right slope for a hill.',
)

const slopeRTool = terrainTool(
  'slope-r',
  'Slope Right',
  Terrain.SLOPE_R,
  'SR',
  null,
  '#3cbcfc',
  'Slope rising to the left.',
)

// ── object tools ──────────────────────────────────────────────────────────

/**
 * The container block in this tile that the held item would load, or null.
 *
 * Loading is the gesture where an item is dropped *onto* an occupied tile
 * instead of beside it, so this is checked before `fits`: the block is what
 * makes the tile occupied, and it is the object the drop is aimed at. A block
 * with its own payload is still a container — the drop replaces that payload,
 * which is the whole point of the gesture — and one that is not (a brick, a
 * hard block) refuses it.
 */
function loadTarget(
  level: Level,
  tx: number,
  ty: number,
  item: ObjectDef,
): PlacedObject | null {
  if (item.payload === undefined) return null
  const hit = objectAtTile(level, tx, ty)
  if (hit === null || hit.def.container !== true) return null
  return hit
}

/**
 * Drop an item into a container block: one more payout of the same item, or a
 * fresh payload when the drop is a different one.
 *
 * Declines at `MAX_BLOCK_CONTENTS`, so an over-full block neither changes nor
 * opens an undo entry, and says so through `ctx.note` rather than silently
 * ignoring the click.
 */
function loadContainer(
  level: Level,
  block: PlacedObject,
  item: ObjectDef,
  ctx: ToolContext,
): boolean {
  const token = item.payload
  if (token === undefined) return false
  // Read the *authored* props, not the runtime object's merged ones: every
  // container declares `contains` as a registry default, so `block.props` says
  // "coin" for a crate nobody has loaded yet — and the first drop would be
  // counted as a repeat.
  const index = level.placementIndexFor(block)
  const authored = index < 0 ? undefined : level.data.objects[index]?.props
  const held =
    authored?.['contains'] === token
      ? (blockUseCount(authored['count']) ?? 1)
      : 0
  if (held >= MAX_BLOCK_CONTENTS) {
    ctx.note = 'BLOCK IS FULL'
    return false
  }
  const count = held + 1
  if (!level.setObjectProps(block, { contains: token, count })) return false
  const name = item.name.toUpperCase()
  ctx.note = count > 1 ? `LOADED ${name} X${count}` : `LOADED ${name}`
  return true
}

const objectTool: Tool = {
  id: 'object',
  name: 'Object',
  kind: 'object',
  cursorTile: Terrain.EMPTY,
  code: 'OB',
  hint: 'Places the selected object. Drag to lay a row of them. Drop an item on a crate to load it.',
  key: 'KeyP',
  mutates: true,
  inPalette: false,
  color: '#58d854',
  canApply: (level, tx, ty, ctx) => {
    const def = ctx.objectDef
    if (def === null) return false
    if (loadTarget(level, tx, ty, def) !== null) return true
    return fits(level, tx, ty, def.w, def.h)
  },
  apply: (level, tx, ty, ctx) => {
    const def = ctx.objectDef
    if (def === null) return false
    const target = loadTarget(level, tx, ty, def)
    if (target !== null) return loadContainer(level, target, def, ctx)
    if (!fits(level, tx, ty, def.w, def.h)) return false
    level.addObject({ id: def.id, tx, ty })
    return true
  },
  footprint: (ctx) => ({ w: ctx.objectDef?.w ?? 1, h: ctx.objectDef?.h ?? 1 }),
}

const objectEraseTool: Tool = {
  id: 'object-erase',
  name: 'Object Eraser',
  kind: 'object',
  cursorTile: Terrain.EMPTY,
  code: 'EX',
  hint: 'Removes the object under the cursor. Shift left click erases too.',
  key: 'KeyB',
  mutates: true,
  inPalette: false,
  color: '#be5861',
  canApply: (level, tx, ty) =>
    inBounds(level, tx, ty) && objectAtTile(level, tx, ty) !== null,
  apply: (level, tx, ty) => {
    const hit = objectAtTile(level, tx, ty)
    if (hit === null) return false
    // Resolve the authoring slot from the stable runtime handle.
    const index = placementIndexOf(level, hit)
    if (index < 0) return false
    level.removeObject(index)
    return true
  },
  footprint: () => ONE,
}

const moveTool: Tool = {
  id: 'move',
  name: 'Select / Move',
  kind: 'object',
  cursorTile: Terrain.EMPTY,
  code: 'MV',
  hint: 'Drag a placed object to another tile. One undo step per drag.',
  key: 'KeyM',
  mutates: true,
  inPalette: false,
  color: '#85ded1',
  canApply: (level, tx, ty) => objectAtTile(level, tx, ty) !== null,
  // The editor drives this one: it needs a drag, not a single tile.
  apply: () => false,
  footprint: () => ONE,
}

// ── utility tools ─────────────────────────────────────────────────────────

const fillTool: Tool = {
  id: 'fill',
  name: 'Rectangle Fill',
  kind: 'utility',
  cursorTile: Terrain.EMPTY,
  code: 'FL',
  hint: 'Drag out a rectangle to flood it with the current brush.',
  key: 'KeyF',
  mutates: true,
  inPalette: false,
  color: '#3cbcfc',
  canApply: () => true,
  apply: () => false,
  footprint: (ctx) => brushFootprint(ctx),
}

const startTool: Tool = {
  id: 'start',
  name: 'Player Start',
  kind: 'utility',
  cursorTile: Terrain.EMPTY,
  code: 'ST',
  hint: 'Move the player start marker. It should be standing on something.',
  key: 'KeyR',
  mutates: true,
  inPalette: false,
  color: '#00a800',
  canApply: (level, tx, ty) => inBounds(level, tx, ty),
  apply: (level, tx, ty) => {
    if (!inBounds(level, tx, ty)) return false
    level.data.startX = tx
    level.data.startY = ty
    return true
  },
  footprint: () => ONE,
}

const eyedropTool: Tool = {
  id: 'eyedrop',
  name: 'Eyedropper',
  kind: 'utility',
  cursorTile: Terrain.EMPTY,
  code: 'ED',
  hint: 'Copy what is under the cursor into the brush. Alt left click reads terrain.',
  key: 'KeyQ',
  mutates: false,
  inPalette: false,
  color: '#f8b8f8',
  canApply: (level, tx, ty) => inBounds(level, tx, ty),
  apply: (level, tx, ty, ctx) => {
    if (!inBounds(level, tx, ty)) return false
    // Objects sit on top of terrain, so the object wins unless the user is
    // explicitly asking for the layer underneath.
    const hit = ctx.preferTerrain ? null : objectAtTile(level, tx, ty)
    ctx.pick.object = hit?.def.id ?? null
    ctx.pick.terrain = level.terrainAt(tx, ty)
    return true
  },
  footprint: () => ONE,
}

/** The footprint of whatever brush is currently loaded. */
export function brushFootprint(ctx: ToolContext): Footprint {
  if (ctx.fillMode === 'object' && ctx.objectDef !== null) {
    return { w: ctx.objectDef.w, h: ctx.objectDef.h }
  }
  return ONE
}

// ── registry ──────────────────────────────────────────────────────────────

/** Every tool, in the order the tool strip draws them. */
export const TOOL_DEFS: Tool[] = [
  groundTool,
  eraseTool,
  hardTool,
  brickTool,
  platformTool,
  slopeLTool,
  slopeRTool,
  objectTool,
  objectEraseTool,
  moveTool,
  fillTool,
  startTool,
  eyedropTool,
]

export const TOOL_BY_ID: ReadonlyMap<string, Tool> = new Map(
  TOOL_DEFS.map((t) => [t.id, t]),
)

/**
 * The terrain brushes, in palette order.
 *
 * Derived from the registry by each tool's own `inPalette` flag rather than kept
 * as a second hand-written list. The flag and the list both claimed to say which
 * brushes are on the terrain tab; they agreed, and nothing checked, so a tool
 * added to one and not the other would put a cell on the tab that selected
 * nothing -- or leave a brush unreachable from the palette. The registry is the
 * one place a tool is declared, so it is the one place the answer comes from.
 */
export const TERRAIN_TOOLS: Tool[] = TOOL_DEFS.filter((t) => t.inPalette)

/** Terrain id -> the terrain brush that paints it (drives the eyedropper). */
export const TOOL_FOR_TERRAIN: ReadonlyMap<number, Tool> = new Map(
  TERRAIN_TOOLS.map((t) => [t.cursorTile, t]),
)

export function toolById(id: string): Tool {
  return TOOL_BY_ID.get(id) ?? groundTool
}

/** Short display name for a terrain id, for the status line. */
const TERRAIN_LABELS: Record<number, string> = {
  [Terrain.EMPTY]: 'AIR',
  [Terrain.GROUND]: 'GROUND',
  [Terrain.HARD]: 'HARD',
  [Terrain.BRICK]: 'BRICK',
  [Terrain.SLOPE_L]: 'SLOPE L',
  [Terrain.SLOPE_R]: 'SLOPE R',
  [Terrain.PLATFORM]: 'PLATFORM',
}

export function terrainLabel(terrain: number): string {
  return TERRAIN_LABELS[terrain] ?? 'TILE'
}

// ── operations the editor composes ────────────────────────────────────────

/**
 * Walk every tile on the line from `(x0,y0)` to `(x1,y1)`, endpoints included.
 *
 * A pointermove can skip a dozen tiles when the mouse is thrown across the
 * screen, and painting only the arrival tile would leave a dotted line behind.
 * This is Bresenham, so the visited tiles form a connected 8-way path with no
 * gaps. `visit` is called for each tile; the caller decides what is legal.
 */
export function lineTiles(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  visit: (tx: number, ty: number) => void,
): void {
  let x = x0
  let y = y0
  const dx = Math.abs(x1 - x0)
  const dy = -Math.abs(y1 - y0)
  const sx = x0 < x1 ? 1 : -1
  const sy = y0 < y1 ? 1 : -1
  let err = dx + dy
  // A course is 160 tiles wide, so 4096 is a runaway guard, never a limit.
  for (let i = 0; i < 4096; i++) {
    visit(x, y)
    if (x === x1 && y === y1) return
    const e2 = 2 * err
    if (e2 >= dy) {
      err += dy
      x += sx
    }
    if (e2 <= dx) {
      err += dx
      y += sy
    }
  }
}

/**
 * Flood a rectangle with the current brush. Returns the number of tiles
 * changed, so the editor can report "nothing to do" instead of burning an
 * undo entry on an empty fill. Pass `dry` to count without mutating, which is
 * how the editor decides whether the fill is worth an undo step.
 */
export function fillRect(
  level: Level,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  ctx: ToolContext,
  dry = false,
): number {
  const ax = Math.min(x0, x1)
  const bx = Math.max(x0, x1)
  const ay = Math.min(y0, y1)
  const by = Math.max(y0, y1)
  const def = ctx.fillMode === 'object' ? ctx.objectDef : null
  let n = 0
  for (let y = ay; y <= by; y++) {
    for (let x = ax; x <= bx; x++) {
      if (def !== null) {
        if (!fits(level, x, y, def.w, def.h)) continue
        if (!dry) level.addObject({ id: def.id, tx: x, ty: y })
      } else {
        if (!inBounds(level, x, y)) continue
        if (!dry) level.setTerrain(x, y, ctx.terrain)
      }
      n++
    }
  }
  return n
}

/**
 * Move a placed object to a new tile, keeping its props. Returns false when
 * the destination is occupied or off the level.
 */
export function moveObject(
  level: Level,
  hit: PlacedObject,
  index: number,
  tx: number,
  ty: number,
): boolean {
  if (tx === hit.tx && ty === hit.ty) return false
  if (!fits(level, tx, ty, hit.def.w, hit.def.h, hit)) return false
  const props = level.data.objects[index]?.props
  // Keep the definition before retiring the old runtime handle.
  const def = hit.def
  level.removeObject(index)
  const placement: {
    id: string
    tx: number
    ty: number
    props?: Record<string, number | string | boolean>
  } = { id: def.id, tx, ty }
  if (props !== undefined) placement.props = { ...props }
  level.addObject(placement)
  return true
}

/**
 * Resolve the index of a placed object inside `level.data.objects`.
 *
 * The bookkeeping lives on `Level` (`placementIndexFor`), which is the module
 * that owns both the data array and the runtime objects and is the only one that
 * can keep them straight after a removal. This is the editor-facing name for it.
 */
export function placementIndexOf(level: Level, o: PlacedObject): number {
  return level.placementIndexFor(o)
}

// ── validation ────────────────────────────────────────────────────────────

/**
 * One code per *meaning*.
 *
 * `start-air` and `goal-air` each used to cover two different problems -- "the
 * start is off the level" and "the start has no floor" -- so the texts told them
 * apart and the codes, which are the part a caller branches on, did not. The
 * off-the-level cases now have codes of their own; `start-air` / `goal-air` keep
 * the meaning they have always had in the test suite, a start or goal standing
 * in the air with nothing under it.
 */
export type IssueCode =
  | 'no-goal'
  | 'start-off'
  | 'start-solid'
  | 'start-air'
  | 'goal-off'
  | 'goal-solid'
  | 'goal-air'

export interface Issue {
  code: IssueCode
  /** Short, uppercase, fits a 256px status line. */
  text: string
}

/**
 * The quality-of-life checks a course has to pass to be playable. Reported in
 * the status line, never modally: the editor should never block the user from
 * saving a work in progress.
 */
export function validateLevel(level: Level): Issue[] {
  const issues: Issue[] = []
  const goal = level.data.objects.find((p) => p.id === 'goal')

  if (goal === undefined) {
    issues.push({ code: 'no-goal', text: 'NO GOAL FLAG' })
  }

  const sx = level.data.startX
  const sy = level.data.startY
  if (!inBounds(level, sx, sy)) {
    issues.push({ code: 'start-off', text: 'START OFF THE LEVEL' })
  } else if (solidAt(level, sx, sy)) {
    issues.push({ code: 'start-solid', text: 'START IS BURIED' })
  } else if (!solidAt(level, sx, sy + 1)) {
    issues.push({ code: 'start-air', text: 'START HAS NO FLOOR' })
  }

  if (goal !== undefined) {
    if (!inBounds(level, goal.tx, goal.ty)) {
      issues.push({ code: 'goal-off', text: 'GOAL OFF THE LEVEL' })
    } else if (solidAt(level, goal.tx, goal.ty)) {
      issues.push({ code: 'goal-solid', text: 'GOAL IS BURIED' })
    } else if (!solidAt(level, goal.tx, goal.ty + 1)) {
      issues.push({ code: 'goal-air', text: 'GOAL HAS NO FLOOR' })
    }
  }

  return issues
}

/** Resize a complete object, keeping a pipe's base planted on the ground. */
export function resizedPlacement(
  level: Level,
  object: PlacedObject,
  requested: number,
): import('../game/types').ObjectPlacement | null {
  const axis = object.def.resize
  if (!axis || !Number.isFinite(requested)) return null
  const minimum =
    axis === 'height' ? 2 : object.def.id.startsWith('moving-platform') ? 2 : 1
  const size = Math.max(minimum, Math.min(32, Math.round(requested)))
  const w = axis === 'width' ? size : object.def.w
  const h = axis === 'height' ? size : object.def.h
  const ty = axis === 'height' ? object.ty + object.def.h - h : object.ty
  if (!fits(level, object.tx, ty, w, h, object)) return null
  const index = placementIndexOf(level, object)
  const original = level.data.objects[index]
  if (!original) return null
  return { ...original, ty, props: { ...original.props, [axis]: size } }
}
